/**
 * Shared profile controller for post-backed profile pages.
 * It centralizes fetch, OG/title updates, query-param edit access, and anonymous ownership checks.
 */
import type { Dispatch, SetStateAction } from "react";
import { useEffect, useState } from "react";
import { AxiosError } from "axios";
import api from "config/axiosConfig";
import { useAnonEntityOwnership } from "hooks/useAnonEntityOwnership";
import { useUser } from "util/UserContext";
import { setDocumentTitle, setEntityOg } from "util/ogMetadataUtils";
import { useSearchParams } from "react-router-dom";
import { apiRoutes } from "util/apiRoutes";
import type { EntityKind } from "util/posts";

interface PostProfileEntityLike {
  id: number | string;
  user_id?: number | string | null;
  title?: string;
  kind?: string | null;
  properties?: {
    privacy?: string;
    [key: string]: unknown;
  };
}

interface UsePostProfileControllerOptions<T extends PostProfileEntityLike> {
  id?: string;
  fallbackKind: EntityKind;
  getResolvedKind?: (entity: T) => EntityKind;
  getDocumentTitle: (entity: T | null) => string;
  getUrlPath?: (entity: T, kind: EntityKind) => string;
  notFoundError: string;
  genericError: string;
}

interface UsePostProfileControllerResult<T extends PostProfileEntityLike> {
  entity: T | null;
  setEntity: Dispatch<SetStateAction<T | null>>;
  error: string;
  isLoading: boolean;
  editMode: boolean;
  setEditMode: Dispatch<SetStateAction<boolean>>;
  hasAnonToken: boolean;
  resolvedKind: EntityKind;
  searchParams: URLSearchParams;
  setSearchParams: ReturnType<typeof useSearchParams>[1];
}

function normalizeUserId(userId?: number | string | null): number | undefined {
  if (typeof userId === "number") {
    return userId;
  }

  if (typeof userId === "string" && userId.trim().length > 0) {
    const parsed = Number(userId);
    return Number.isNaN(parsed) ? undefined : parsed;
  }

  return undefined;
}

export function usePostProfileController<T extends PostProfileEntityLike>({
  id,
  fallbackKind,
  getResolvedKind,
  getDocumentTitle,
  getUrlPath,
  notFoundError,
  genericError,
}: UsePostProfileControllerOptions<T>): UsePostProfileControllerResult<T> {
  const { currentUser } = useUser();
  const [entity, setEntity] = useState<T | null>(null);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const resolvedKind = entity ? (getResolvedKind?.(entity) ?? fallbackKind) : fallbackKind;
  const hasAnonToken = useAnonEntityOwnership(
    resolvedKind,
    entity?.id,
    normalizeUserId(entity?.user_id),
    entity?.properties?.privacy
  );

  useEffect(() => {
    if (!id || Number.isNaN(Number(id))) {
      setError(notFoundError);
      setIsLoading(false);
      return;
    }

    let isCancelled = false;

    const loadEntity = async () => {
      setIsLoading(true);

      try {
        const response = await api.get<T>(apiRoutes.posts.byId(id));
        if (isCancelled) {
          return;
        }

        setEntity(response.data);
        setError("");

        if (searchParams.get("edit") === "true") {
          const ownsEntity = currentUser?.id === normalizeUserId(response.data.user_id);
          if (ownsEntity) {
            setEditMode(true);
            setSearchParams({});
          }
        }
      } catch (loadError) {
        if (isCancelled) {
          return;
        }

        if (loadError instanceof AxiosError && loadError.response?.status === 404) {
          setError(notFoundError);
        } else {
          setError(genericError);
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    };

    void loadEntity();

    return () => {
      isCancelled = true;
    };
  }, [id, searchParams, currentUser, setSearchParams, notFoundError, genericError]);

  useEffect(() => {
    const pageTitle = getDocumentTitle(entity);
    if (pageTitle.trim().length > 0) {
      setDocumentTitle(pageTitle);
    }

    if (!entity) {
      return;
    }

    const entityKind = getResolvedKind?.(entity) ?? fallbackKind;
    const urlPath = getUrlPath
      ? getUrlPath(entity, entityKind)
      : `/${entityKind}/${encodeURIComponent(String(entity.id))}`;

    setEntityOg({
      entityType: entityKind,
      entity,
      urlPath,
    });
  }, [entity, fallbackKind, getDocumentTitle, getResolvedKind, getUrlPath]);

  useEffect(() => {
    if (searchParams.get("edit") === "true" && entity && hasAnonToken && !editMode) {
      setSearchParams({});
      setEditMode(true);
    }
  }, [entity, hasAnonToken, editMode, searchParams, setSearchParams]);

  return {
    entity,
    setEntity,
    error,
    isLoading,
    editMode,
    setEditMode,
    hasAnonToken,
    resolvedKind,
    searchParams,
    setSearchParams,
  };
}
